import { Button, Dialog, DialogActions, DialogContent, FormControlLabel, Link, Grid, Radio, RadioGroup, Stack } from '@mui/material'
import { isNil, isUndefined, orderBy } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import ApplicationConf from '../../../conf/ApplicationConf'
import EventsAction from '../../../events/actions/EventsAction'
import { EVENT_TYPES_CODES } from '../../../events/constants/EventsConstants'
import { PATH_STATION } from '../../../home/constants/RouteConstants'
import InstallationAction from '../../../installation/actions/InstallationAction'
import { getDate } from '../../../utils/DateUtil'
import { exportFile } from '../../../utils/ExportDataUtil'
import { createIconMaterialAssignment, createIconMaterialState, getAssignmentLabel } from '../../../utils/MaterielUtils'
import { hasValue } from '../../../utils/NumberUtil'
import { getStationTypeFromName, getStationTypeNameFromTypeCode } from '../../../utils/StationUtils'
import { getLabel } from '../../../utils/StoreUtils'
import MaterielAction from '../../actions/MaterielAction'
import DtoMaterielSituation from '../../dto/DtoMaterielSituation'
import DtoVariousMaterielSituation from '../variousMateriel/dto/DtoVariousMaterielSituation'
import SituationForm from './form/SituationForm'
import { darkBlue } from 'utils/constants/ColorTheme'
import { CardTable } from 'components/datatable/NewTable'
import { STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import { MATERIEL_STATE_ON_SITE_ID, MATERIEL_STATE_PROVISIONAL_ID } from 'materiel/constants/MaterielStateConstants'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { push } from '@lagunovsky/redux-react-router'

const headers = ['assignment', 'siteName', 'statusCode', 'comment', 'administrator', 'situationDate', 'situationEndDate']

const STEP = {
    SITUATION: 0,
    LINK: 1,
    EVENT: 2,
}

const SituationPanel = ({
    materielSituation = [],
    active = false,
    idMateriel,
    serialNumber,
    purchaseDate,
    idKey,
    isCentral = false,
    lastSituations = [],
    fetchPreviousParam = () => {},
    materials,
    saveFunction = () => {},
    savePreviousParam = () => {},
    deleteFunction = () => {},
}) => {
    const [situation, setSituation] = useState({ situationDate: materielSituation.length ? moment().valueOf() : purchaseDate })
    const [situationMaterialReplace, setSituationMaterialReplace] = useState({})
    const [shouldReplace, setShouldReplace] = useState(false)
    const [shouldUsePreviousParam, setShouldUsePreviousParam] = useState(false)
    const [openPopup, setOpenPopup] = useState(false)
    const [step, setStep] = useState(0)

    const {
        materielStates,
        piezometers,
        qualitometers,
        pluviometers,
        hydrometricStations,
        installations,
        stationsEvents,
        contributors,
    } = useSelector(store => ({
        materielStates: store.MaterielReducer.materielStates,
        piezometers: store.PiezometryReducer.piezometersLight,
        qualitometers: store.QualityReducer.qualitometersLight,
        pluviometers: store.PluviometryReducer.pluviometers,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        installations: store.InstallationReducer.installations,
        stationsEvents: store.MaterielReducer.stationsEvents,
        contributors: store.ContributorReducer.contributors,
    }), shallowEqual)

    const dispatch = useDispatch()

    const getStationsByType = useCallback(siteType => {
        switch (siteType) {
            case STATION_TYPE_CONSTANT.PIEZOMETER:
                return piezometers
            case STATION_TYPE_CONSTANT.pluviometer:
                return pluviometers
            case STATION_TYPE_CONSTANT.QUALITOMETER:
                return qualitometers
            case STATION_TYPE_CONSTANT.HYDROMETRIC_STATION:
                return hydrometricStations
            case STATION_TYPE_CONSTANT.INSTALLATION:
                return installations
            default:
                return []
        }
    }, [piezometers, pluviometers, qualitometers, hydrometricStations, installations])

    const resetState = () => {
        setSituation({ situationDate: materielSituation.length ? moment().valueOf() : purchaseDate })
        setSituationMaterialReplace({})
        setShouldReplace(false)
        setShouldUsePreviousParam(false)
    }

    useEffect(() => {
        if (!materielStates.length) {
            dispatch(MaterielAction.fetchMaterielStates())
        }
    }, [])

    const datas = useMemo(() => {
        if (materielSituation.length) {
            return orderBy(materielSituation, 'situationEndDate', 'desc').map(situ => {
                const findState = materielStates.find(state => state.code === situ.statusCode) || {}
                const siteType = (() => {
                    if (situ.siteType) {
                        return getStationTypeNameFromTypeCode(situ.siteType)
                    }
                    return ''
                })()
                const link = (() => {
                    if (siteType) {
                        const linkTypeName = getStationTypeFromName(siteType)
                        if (linkTypeName) {
                            return `${PATH_STATION}/${linkTypeName}/${situ.siteCode}`
                        }
                    }
                    return ''
                })()
                const comment = (() => {
                    const result = situ.comment
                    if (result && result.length >= 50) {
                        return `${result.substring(0, 47)}...`
                    }
                    return result
                })()
                const administrator = getLabel(contributors, situ.administratorCode)
                const site = getStationsByType(situ.siteType).find(s => s.id === situ.siteCode) || {}
                const siteName = getAssignmentLabel(site, situ.siteName)
                return {
                    statusCode: createIconMaterialState(findState.label, findState.code),
                    stateLabel: findState.label,
                    stateCode: situ.statusCode,
                    siteCode: situ.siteCode,
                    assignment: siteType && situ.siteName ? createIconMaterialAssignment(i18n[siteType], situ.siteType) : '',
                    siteName: !siteName || isNil(situ.siteCode) ? siteName : <Link onClick={() => dispatch(push(`${link}/materiel`))}>{siteName}</Link>,
                    link,
                    comment,
                    situationDate: getDate(situ.situationDate),
                    situationEndDate: getDate(situ.situationEndDate),
                    administrator,
                    id: situ.id,
                }
            })
        }
        return []
    }, [materielSituation, getStationsByType])

    const addEvent = (siteType, siteCode, event) => {
        if (siteType === STATION_TYPE_CONSTANT.INSTALLATION) {
            return dispatch(InstallationAction.addInstallationEvent(siteCode, event))
        }
        return dispatch(EventsAction.addEvent(siteType, siteCode, event))
    }

    const addSituation = (situ, event, situMatReplace) => {
        addEvent(situ.siteType, situ.siteCode, event).then(eventCode => {
            if (eventCode) {
                saveFunction({ ...situ, eventCode })
                if (shouldReplace) {
                    saveFunction({ ...situMatReplace, eventCode }, false)
                }
            }
        })
    }

    const onSave = () => {
        if (materielSituation.some(({ situationEndDate, situationDate }) => (situationEndDate || situationDate) > situation.situationDate)) {
            dispatch(ToastrAction.warning(i18n.theMaterielAlreadyHasASituationOnThisDateRange))
            return
        }
        const situationFormat = { ...situation, [idKey]: idMateriel }
        const situationMaterialReplaceFormat = { ...situationMaterialReplace, [idKey]: situationMaterialReplace.materielId, situationDate: situationFormat.situationDate }
        if (shouldUsePreviousParam) {
            savePreviousParam()
        }
        switch (step) {
            case STEP.LINK:
                const eventTech = {
                    date: situation.situationDate,
                    eventType: 'T',
                    graph: '0',
                    bankExport: '0',
                    eventHour: situation.situationDate,
                    code: situation.siteCode,
                }
                addSituation(situationFormat, eventTech, situationMaterialReplaceFormat)
                break
            case STEP.EVENT:
                if (shouldReplace) {
                    saveFunction(situationMaterialReplaceFormat, false)
                }
                saveFunction(situationFormat)
                break
            default:
                saveFunction(situationFormat)
        }
        resetState()
        setOpenPopup(false)
        setStep(STEP.SITUATION)
    }

    const onExportSituation = () => {
        exportFile({
            data: datas.map(d => ({ ...d, statusCode: { value: d.stateCode, format: '0', cellType: 'number' }, status: d.stateLabel, headers: ['assignment', 'siteName', 'statusCode', 'status', 'comment', 'administrator', 'situationDate', 'situationEndDate'] })),
            exportType: 'xlsx',
            titleFile: i18n.situation,
        })
    }

    const getStations = () => getStationsByType(situation.siteType)

    const onChange = obj => {
        if (obj?.siteCode !== situation.siteCode) {
            const eventUrl = (() => {
                const siteId = obj.siteCode
                switch (obj.siteType) {
                    case STATION_TYPE_CONSTANT.PIEZOMETER:
                        return ApplicationConf.piezometer.events(siteId)
                    case STATION_TYPE_CONSTANT.pluviometer:
                        return ApplicationConf.pluviometry.events(siteId)
                    case STATION_TYPE_CONSTANT.QUALITOMETER:
                        return ApplicationConf.qualitometer.events(siteId)
                    case STATION_TYPE_CONSTANT.HYDROMETRIC_STATION:
                        return ApplicationConf.hydrometricStation.events(siteId)
                    case STATION_TYPE_CONSTANT.INSTALLATION:
                        return ApplicationConf.installation.events(siteId)
                    default:
                        return ''
                }
            })()
            if (eventUrl) {
                dispatch(MaterielAction.fetchMaterielStationEvents(eventUrl, obj.siteType))
            }
        }
        if (obj.shouldUsePreviousParam) {
            fetchPreviousParam(situationMaterialReplace.materielId)
        }
        setSituation(obj)
    }

    const getStationEvent = () => stationsEvents.filter(e =>
        e.code === situation.siteCode
        && e.siteType === situation.siteType
        && (e.eventType === EVENT_TYPES_CODES.PREVENTIVE || e.eventType === EVENT_TYPES_CODES.CURATIVE),
    )

    const getPopupPanel = () => {
        switch (step) {
            case STEP.SITUATION:
                const stations = getStations()
                const station = stations.find(s => s.id === situation.siteCode)
                const lastMaterialOnSite = station ? lastSituations.filter(s => s.siteCode === station.id && !s.situationEndDate) : []
                const materialOnSite = lastMaterialOnSite.map(s => materials.find(m => m.id === s.materielId)).filter(m => !!m)
                return (
                    <SituationForm
                        situation={situation}
                        serialNumber={serialNumber}
                        showCheckbox={!!lastMaterialOnSite.length}
                        shouldReplace={shouldReplace}
                        isCentral={isCentral}
                        shouldUsePreviousParam={shouldUsePreviousParam}
                        materialOnSite={materialOnSite}
                        situationMaterialReplace={situationMaterialReplace}
                        stations={getStations()}
                        setSituation={onChange}
                        setSituationMaterialReplace={setSituationMaterialReplace}
                        setShouldUsePreviousParam={setShouldUsePreviousParam}
                        setShouldReplace={setShouldReplace}
                    />
                )
            case 1:
                const eventNumber = getStationEvent().length
                return (
                    <Stack spacing={2} alignItems={'center'}>
                        <Button sx={{ width: '250px' }} variant={'contained'} disabled={!eventNumber} onClick={() => setStep(STEP.EVENT)}>{i18n.assignEvent}</Button>
                        <Button sx={{ width: '250px' }} variant={'contained'} onClick={() => onSave()}>{i18n.doNotLink}</Button>
                    </Stack>
                )
            case 2:
                const events = orderBy(getStationEvent(), 'date', 'desc')
                return (
                    <Stack spacing={2}>
                        <RadioGroup>
                            {
                                events.map((e, i) => (
                                    <FormControlLabel
                                        key={i}
                                        value={e.number}
                                        control={<Radio />}
                                        label={`${getDate(e.date)} - ${e.comment}`}
                                        onChange={() => {
                                            setSituation({ ...situation, eventCode: e.number })
                                            setSituationMaterialReplace({ ...situationMaterialReplace, eventCode: e.number })
                                        }}
                                    />
                                ))
                            }
                        </RadioGroup>
                    </Stack>
                )
            default:
                return null
        }
    }

    const getValidateButton = () => {
        const isBlocked = !hasValue(situation.statusCode) || !hasValue(situation.situationDate) || ((situation.statusCode === MATERIEL_STATE_ON_SITE_ID || situation.statusCode === MATERIEL_STATE_PROVISIONAL_ID) && !situation.siteCode) || (shouldReplace && isUndefined(situationMaterialReplace.statusCode)) || (step === STEP.EVENT && !situation.eventCode)
        return (
            !step && (situation.statusCode === MATERIEL_STATE_ON_SITE_ID || situation.statusCode === MATERIEL_STATE_PROVISIONAL_ID) ? (
                <Button disabled={isBlocked} onClick={() => !isBlocked && setStep(STEP.LINK)} variant='outlined' >
                    {i18n.next}
                </Button>
            ) : (
                <Button disabled={isBlocked} onClick={() => !isBlocked && onSave()} variant='contained' >
                    {i18n.validate}
                </Button>
            )
        )
    }

    if (!idMateriel) {
        return null
    }
    const actions = [
        {
            icon: 'new_label',
            tooltip: i18n.newSituation,
            color: 'white',
            onClick: () => setOpenPopup(true),
            displayed: active,
        },
        {
            icon: 'file_download',
            tooltip: i18n.export,
            color: 'white',
            onClick: () => onExportSituation(),
            displayed: active,
        },
    ]
    const lineActions = [
        {
            icon: 'delete',
            tooltip: i18n.delete,
            onClick: ({ id }) => deleteFunction(id),
            displayed: active,
        },
    ]
    return (
        <Grid size={12}>
            <CardTable
                title={i18n.materielHistory}
                color={darkBlue}
                rows={datas}
                headers={headers}
                actions={actions}
                lineActions={lineActions}
            />
            <Dialog
                fullWidth
                maxWidth='lg'
                open={openPopup}
            >
                <DefaultDialogTitle
                    title={i18n.newSituation}
                    onClose={() => {
                        setOpenPopup(false)
                        setStep(STEP.SITUATION)
                        resetState()
                    }}
                />
                <DialogContent>
                    {getPopupPanel()}
                </DialogContent>
                <DialogActions>
                    {
                        step !== STEP.SITUATION && (
                            <Button
                                onClick={() => {
                                    setStep(step < STEP.EVENT ? STEP.SITUATION : STEP.LINK)
                                }}
                                variant='outlined'
                            >
                                { i18n.return }
                            </Button>
                        )
                    }
                    {(step !== STEP.LINK && getValidateButton())}
                </DialogActions>
            </Dialog>
        </Grid>
    )
}

SituationPanel.propTypes = {
    materielSituation: PropTypes.arrayOf(PropTypes.instanceOf(DtoMaterielSituation)),
    active: PropTypes.bool,
    idKey: PropTypes.string,
    idMateriel: PropTypes.number,
    serialNumber: PropTypes.string,
    purchaseDate: PropTypes.number,
    lastSituations: PropTypes.arrayOf(PropTypes.instanceOf(DtoVariousMaterielSituation)),
    materials: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
    })),
    saveFunction: PropTypes.func,
    deleteFunction: PropTypes.func,
    isCentral: PropTypes.bool,
    savePreviousParam: PropTypes.func,
    fetchPreviousParam: PropTypes.func,
}

export default SituationPanel
